import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Plus, Edit, Coins, Briefcase, MoreVertical, UserPlus, Users, Trash2, Eye, EyeOff, Search, X } from 'lucide-react';

export const OrganizationsTab = ({
  organizations, users, theme, creditsByEntity = {},
  onAddOrg, onEditOrg, onToggleActive, onDelete, onAddCredits, onAddUserToOrg
}) => {
  const { cardBg, cardBorder, innerCardBg } = theme;
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOrgs = useMemo(() => {
    if (!searchQuery.trim()) return organizations;
    const q = searchQuery.toLowerCase();
    return organizations.filter(org =>
      org.name?.toLowerCase().includes(q) ||
      org.billing_email?.toLowerCase().includes(q) ||
      org.gstin?.toLowerCase().includes(q) ||
      org.phone?.toLowerCase().includes(q)
    );
  }, [organizations, searchQuery]);

  return (
    <Card className={`${cardBg} ${cardBorder}`} data-testid="organizations-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading">Organization Management</CardTitle>
        <Button className="bg-primary hover:bg-primary/90" size="sm" onClick={onAddOrg} data-testid="add-org-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Organization
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, GSTIN, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
              data-testid="orgs-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">{filteredOrgs.length} of {organizations.length} organizations</p>
        <div className="space-y-4">
          {filteredOrgs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{searchQuery ? 'No organizations match your search' : 'No organizations yet'}</p>
          ) : (
            filteredOrgs.map(org => {
              const orgUsers = users.filter(u => u.organization_id === org.id);
              return (
                <div key={org.id} className={`p-5 rounded-xl ${innerCardBg} border ${cardBorder} ${!org.is_active ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Briefcase className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-heading font-semibold text-lg">{org.name}</h4>
                        <p className="text-sm text-muted-foreground">{org.billing_email}</p>
                        {org.gstin && <p className="text-xs text-muted-foreground mt-1">GSTIN: {org.gstin}</p>}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge variant="outline" className={cardBorder}><Users className="w-3 h-3 mr-1" /> {orgUsers.length} Users</Badge>
                          <Badge variant={org.is_active ? 'default' : 'secondary'}>{org.is_active ? 'Active' : 'Inactive'}</Badge>
                          {creditsByEntity[org.id] && (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30" data-testid={`org-credits-badge-${org.id}`}>
                              <Coins className="w-3 h-3 mr-1" />
                              <span className="text-blue-700 font-semibold">{Number(creditsByEntity[org.id].balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })}M</span>
                              <span className="mx-1 opacity-50">·</span>
                              <span className="font-semibold">{Number(creditsByEntity[org.id].topup_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })}T</span>
                              {creditsByEntity[org.id].used_in_period > 0 && <span className="ml-1 text-xs opacity-70">· {Number(creditsByEntity[org.id].used_in_period).toLocaleString('en-IN', { maximumFractionDigits: 0 })} used (30d)</span>}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className={cardBorder} onClick={() => onAddCredits(org.id)}>
                        <Coins className="w-4 h-4 mr-1" /> Credits
                      </Button>
                      <Button variant="outline" size="sm" className={cardBorder} onClick={() => onAddUserToOrg(org.id)}>
                        <UserPlus className="w-4 h-4 mr-1" /> User
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEditOrg(org)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onToggleActive(org)}>
                            {org.is_active ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                            {org.is_active ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onDelete(org)} className="text-red-500 focus:text-red-500">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {orgUsers.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <p className="text-sm font-medium mb-2">Team Members:</p>
                      <div className="flex flex-wrap gap-2">
                        {orgUsers.slice(0, 5).map(u => (
                          <div key={u.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-background">
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-[10px] font-medium text-primary">{u.name?.charAt(0)}</span>
                            </div>
                            <span className="text-sm">{u.name}</span>
                          </div>
                        ))}
                        {orgUsers.length > 5 && <span className="text-sm text-muted-foreground px-3 py-1.5">+{orgUsers.length - 5} more</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};
